import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OperatorInput, OperatorOutput } from './models/models';
import { IOperatorClass } from './OperatorClass';
import { ExcelUtility } from '../../../../../shared/ExcelUtility';
import { GridConfigService } from '../../../../grid-configuration/data-access/grid-configuration.service';
import { Labels } from '../../../../../shared/labels';

export class FileValidator implements IOperatorClass {
    private gridConfigService = inject(GridConfigService);

    private readonly REQUIRED_SHEET_COUNT = 10;

    private readonly SHEET_NAMES = [
        'Company Information',
        'Group Reporting',
        'Annual Statement',
        Labels.premium.directPlural,
        Labels.premium.supplementalPlural,
        'UD Columns',
        Labels.premium.udDirect,
        Labels.premium.udSupplemental,
        'Comments',
        'Meta' ];
    private readonly COMPANY_INFO_SHEET_NAME = 'Company Information';
    private readonly COMPANY_INFO_ROW_LABELS = [
        'ISO Billing Number',
        'Company Name',
        "Preparer's Name",
        'Email Address',
        'Additional Email Address',
        'Title',
        'Address',
        'City',
        'State',
        'Zip Code',
        'Phone Number',
    ];
    private readonly VERSION: string = "v1.2";

    async runOperator(operatorInput: OperatorInput): Promise<OperatorOutput> {
        let workbook: any;

        try {
            workbook = await ExcelUtility.load(operatorInput.file);
        } catch {
            return {
                status: 'error',
                message: ['The file could not be read. Please upload a valid Excel file.'],
                operatorInput
            };
        }

        const workbookResult = this.validateWorkbook(workbook,operatorInput);
        if (workbookResult) {
            return workbookResult;
        }

        const sheet1Result = this.validateSheet1(workbook.worksheets(0));
        if (sheet1Result) {
            return sheet1Result;
        }

        const isoBillingNumber = String(workbook.worksheets(0).rows(0).cells(1).value ?? '').trim();
        operatorInput.prfnumber = isoBillingNumber;
        operatorInput.prfGroup = isoBillingNumber.substring(0, 4);
        operatorInput.workbook = workbook;
        operatorInput.fileConfig = this.getFileConfig(workbook);


        const gridConfig = await firstValueFrom(
            this.gridConfigService.getAllConfigurationsForYear(operatorInput.prfyear)
        );

        const sheet4Result = this.validateSheet4(workbook.worksheets(3), gridConfig);
        if (sheet4Result) {
            return sheet4Result;
        }

        const sheet5Result = this.validateSheet5(workbook.worksheets(4), gridConfig);
        if (sheet5Result) {
            return sheet5Result;
        }

        const sheet6Result = this.validateSheet6(workbook.worksheets(5));
        if (sheet6Result) {
            return sheet6Result;
        }

        const sheet7Result = this.validateSheetIntital(workbook.worksheets(6));
        if (sheet7Result) {
            return sheet7Result;
        }

        const sheet8RawResult = this.validateSheetIntital(workbook.worksheets(7));
        if (sheet8RawResult) {
            return sheet8RawResult;
        }

        
        const sheet7ResultEx = this.validateSheet7Ex(
            workbook.worksheets(6),
            workbook.worksheets(2),
            operatorInput.fileConfig
        );
        if (sheet7ResultEx) {
            return sheet7ResultEx;
        }
        

        const sheet8Result = this.validateSheet8(
            workbook.worksheets(7),
            workbook.worksheets(2),
            operatorInput.fileConfig
        );
        if (sheet8Result) {
            return sheet8Result;
        }

        return { status: 'success', operatorInput };
    }
    

    private validateWorkbook(workbook: any, operatorInput: OperatorInput): OperatorOutput | null {
        if (workbook.worksheets().count !== this.REQUIRED_SHEET_COUNT) {
            return {
                status: 'error',
                message: [
                    `Invalid file: expected ${this.REQUIRED_SHEET_COUNT} sheets but found ${workbook.worksheets().count}.`,
                ],
                operatorInput:  operatorInput
            };
        }
        for (let i = 0; i < this.SHEET_NAMES.length; i++) {
            const worksheet = workbook.worksheets(i);
            if (worksheet.name !== this.SHEET_NAMES[i]) {
                return {
                        status: 'error',
                        message: [`Invalid file: one or more sheets are missing or incorrectly named. [ ${this.SHEET_NAMES[i]} ]`],
                        operatorInput:  operatorInput
                    };
            }
        }
        // check meta version
        var meta_sheet = workbook.worksheets(this.SHEET_NAMES.length - 1);
        var version_cell = meta_sheet.rows(6).cells(0).value;
        if (version_cell == null || String(version_cell).trim() === '' || String(version_cell).trim() !== this.VERSION) {
            return {
                status: 'error',
                message: ['Invalid file: version missmatch '],
                operatorInput: operatorInput
            };
        }

        return null;
    }

    private validateSheet1(worksheet: any): OperatorOutput | null {
        if (worksheet.name !== this.COMPANY_INFO_SHEET_NAME) {
            return {
                status: 'error',
                message: [
                    `Invalid file: sheet 1 should be named "${this.COMPANY_INFO_SHEET_NAME}" but found "${worksheet.name}".`,
                ],
            };
        }

        for (let row = 0; row < this.COMPANY_INFO_ROW_LABELS.length; row++) {
            const cellValue = worksheet.rows(row).cells(0).value;
            const expected = this.COMPANY_INFO_ROW_LABELS[row];
            const actual = cellValue != null ? String(cellValue).trim() : '';
            if (actual !== expected) {
                return {
                    status: 'error',
                    message: [
                        `Invalid file: row ${row + 1} in "${this.COMPANY_INFO_SHEET_NAME}" should be "${expected}" but found "${actual}".`,
                    ],
                };
            }
        }

        return null;
    }

    private validateSheet4(
        worksheet: any,
        gridConfig: any
    ): OperatorOutput | null {
        const configStates = gridConfig[0].states.State ?? [];
        const configColumns = gridConfig[0].cols?.Col ?? [];

        for (let row = 0; row < configStates.length; row++) {
            const cellValue = worksheet.rows(row + 1).cells(1).value;
            const actual = cellValue != null ? String(cellValue).trim() : '';
            const expected = configStates[row].Name.trim();
            if (!expected.startsWith(actual)) {
                return {
                    status: 'error',
                    message: [
                        `Invalid file: state row ${row + 1} should be "${expected}" but found "${actual}".`,
                    ],
                };
            }
        }

        for (let col = 1; col < configColumns.length; col++) {
            const cellValue = worksheet.rows(0).cells(col + 1).value;
            const actual = cellValue != null ? String(cellValue).replace(/\r\n|\r|\n/g, ' ').trim() : '';
            const colObj = configColumns[col];
            const expected = [colObj.Number, colObj.Name1, colObj.Name2, colObj.Name3, colObj.Name4]
                .filter((v: string) => v != null && String(v).trim() !== '')
                .join(' ');

            if (actual !== expected) {
                return {
                    status: 'error',
                    message: [
                        `Invalid file: column ${col + 1} header should be "${expected}" but found "${actual}".`,
                    ],
                };
            }
        }

        return null;
    }

    private validateSheet5(
        worksheet: any,
        gridConfig: any
    ): OperatorOutput | null {
        const configStates = gridConfig[0].states.State ?? [];
        const configColumns = gridConfig[0].cols?.Col ?? [];

        for (let row = 0; row < configStates.length; row++) {
            const cellValue = worksheet.rows(row + 1).cells(1).value;
            const actual = cellValue != null ? String(cellValue).trim() : '';
            const expected = configStates[row].Name.trim();
            if (!expected.startsWith(actual) && row != 63 ) {
                return {
                    status: 'error',
                    message: [
                        `Invalid file: state row ${row + 1} should be "${expected}" but found "${actual}".`,
                    ],
                };
            }
        }

        for (let col = 1; col < configColumns.length; col++) {
            const cellValue = worksheet.rows(0).cells(col + 1).value;
            const actual = cellValue != null ? String(cellValue).replace(/\r\n|\r|\n/g, ' ').trim() : '';
            const colObj = configColumns[col];
            const expected = [colObj.Number, colObj.Name1, colObj.Name2, colObj.Name3, colObj.Name4]
                .filter((v: string) => v != null && String(v).trim() !== '')
                .join(' ');

            if (actual !== expected) {
                return {
                    status: 'error',
                    message: [
                        `Invalid file: column ${col + 1} header should be "${expected}" but found "${actual}".`,
                    ],
                };
            }
        }

        return null;
    }
    private validateSheet6(worksheet: any): OperatorOutput | null {
        const UD_COLUMNS_SHEET_NAME = 'UD Columns';
        const HEADLINE1_COL = 0;
        const IS_ISO_MAPPING_COL = 4;
        const ISO_MAPPING_COL = 5;
        let rowIndex = 1;

        while (true) {
            const headline1 = worksheet.rows(rowIndex).cells(HEADLINE1_COL).value;
            if (headline1 == null || String(headline1).trim() === '') break;

            const isIsoMapping = worksheet.rows(rowIndex).cells(IS_ISO_MAPPING_COL).value;
            
            if (isIsoMapping != null && String(isIsoMapping).trim().toUpperCase() === 'YES') {
                const isoMapping = worksheet.rows(rowIndex).cells(ISO_MAPPING_COL).value;
                if (isoMapping == null || String(isoMapping).trim() === '') {
                    return {
                        status: 'error',
                        message: [
                            `Invalid file: row ${rowIndex} - ISO Mapping value is required in "${UD_COLUMNS_SHEET_NAME}" when ISO Mapping is selected.`,
                        ],
                    };
                }
            }
            rowIndex++;
        }

        return null;
    }

    private validateSheet7Ex(
        udSupplementalSheet: any,
        annualStatementSheet: any,
        fileConfig: any
    ): OperatorOutput | null {
        const UD_DIRECT_SHEET_NAME = Labels.premium.udDirect;
        const UD_COL_D = 3;
        const AG_WRITE_ROW_INDEX: number = fileConfig?.['agwritecol'] ?? 24;
        const AG_WRITE_COL_C = 2;
        const AG_WRITE_COL_D = 3;

        // Check whether any data row in UD Supplemental has a value in column D
        let udDirectHasColD = false;
        let rowIndex = 1;
        while (true) {
            const rowLabel = udSupplementalSheet.rows(rowIndex).cells(0).value;
            if (rowLabel == null || String(rowLabel).trim() === '') break;
            const colD = udSupplementalSheet.rows(rowIndex).cells(UD_COL_D).value;
            if (colD != null && String(colD).trim() !== '' && String(colD).trim() !== '0') {
                udDirectHasColD = true;
                break;
            }
            rowIndex++;
        }

        if (!udDirectHasColD) {
            return null;
        }

        // Column D data exists in UD Direct — ensure Annual Statement has
        // Aggregate Write-ins premium entered (col C or col D of that row).
        const agWriteC = annualStatementSheet.rows(AG_WRITE_ROW_INDEX).cells(AG_WRITE_COL_C).value;
        const agWriteD = annualStatementSheet.rows(AG_WRITE_ROW_INDEX).cells(AG_WRITE_COL_D).value;
        const agWriteCStr = agWriteC != null ? String(agWriteC).trim() : '';
        const agWriteDStr = agWriteD != null ? String(agWriteD).trim() : '';

        const agWriteHasValue =
            (agWriteCStr !== '' && agWriteCStr !== '0') ||
            (agWriteDStr !== '' && agWriteDStr !== '0');

        if (!agWriteHasValue) {
            return {
                status: 'error',
                message: [
                    `Invalid file: "${UD_DIRECT_SHEET_NAME}" has premiums entered in Column Aggregate Write-ins but` +
                    ` the Annual Statement does not have a value in the Aggregate Write-ins row.` +
                    ` Please enter Aggregate Write-ins premium in the Annual Statement sheet before adding ${UD_DIRECT_SHEET_NAME} Column Aggregate Write-ins data.`,
                ],
            };
        }

        return null;
    }

    private validateSheet8(
        udSupplementalSheet: any,
        annualStatementSheet: any,
        fileConfig: any
    ): OperatorOutput | null {
        const UD_SUPPLEMENTAL_SHEET_NAME = Labels.premium.udSupplemental;
        const UD_COL_D = 3;
        const AG_WRITE_ROW_INDEX: number = fileConfig?.['agwritecol'] ?? 24;
        const AG_WRITE_COL_C = 2;
        const AG_WRITE_COL_D = 3;

        // Check whether any data row in UD Supplemental has a value in column D
        let udSupplementalHasColD = false;
        let rowIndex = 1;
        while (true) {
            const rowLabel = udSupplementalSheet.rows(rowIndex).cells(0).value;
            if (rowLabel == null || String(rowLabel).trim() === '') break;
            const colD = udSupplementalSheet.rows(rowIndex).cells(UD_COL_D).value;
            if (colD != null && String(colD).trim() !== '' && String(colD).trim() !== '0') {
                udSupplementalHasColD = true;
                break;
            }
            rowIndex++;
        }

        if (!udSupplementalHasColD) {
            return null;
        }

        // Column D data exists in UD Supplemental — ensure Annual Statement has
        // Aggregate Write-ins premium entered (col C or col D of that row).
        const agWriteC = annualStatementSheet.rows(AG_WRITE_ROW_INDEX).cells(AG_WRITE_COL_C).value;
        const agWriteD = annualStatementSheet.rows(AG_WRITE_ROW_INDEX).cells(AG_WRITE_COL_D).value;
        const agWriteCStr = agWriteC != null ? String(agWriteC).trim() : '';
        const agWriteDStr = agWriteD != null ? String(agWriteD).trim() : '';

        const agWriteHasValue =
            (agWriteCStr !== '' && agWriteCStr !== '0') ||
            (agWriteDStr !== '' && agWriteDStr !== '0');

        if (!agWriteHasValue) {
            return {
                status: 'error',
                message: [
                    `Invalid file: "${UD_SUPPLEMENTAL_SHEET_NAME}" has premiums entered in Aggregate Write-ins but` +
                    ` the Annual Statement does not have a value in the Aggregate Write-ins row.` +
                    ` Please enter Aggregate Write-ins premium in the Annual Statement sheet before adding ${UD_SUPPLEMENTAL_SHEET_NAME} Column Aggregate Write-ins data.`,
                ],
            };
        }

        return null;
    }

    private validateSheetIntital(worksheet: any): OperatorOutput | null {
        const MAX_COLS = 50;
        const MAX_ROWS = 500;

        // Collect which column indices have a header value in row 0 (skip first column)
        const headerColumns = new Set<number>();
        for (let col = 1; col < MAX_COLS; col++) {
            const headerValue = worksheet.rows(0).cells(col).value;
            if (headerValue != null && String(headerValue).trim() !== '') {
                headerColumns.add(col);
            }
        }

        // Scan data rows; for any non-empty cell whose column has no header, report an error (skip first column)
        for (let row = 1; row < MAX_ROWS; row++) {
            let rowHasAnyData = false;
            for (let col = 1; col < MAX_COLS; col++) {
                const cellValue = worksheet.rows(row).cells(col).value;
                if (cellValue != null && String(cellValue).trim() !== '' && String(cellValue).trim() !== '0') {
                    rowHasAnyData = true;
                    if (!headerColumns.has(col)) {
                        const colLetter = this.getColumnLetter(col);
                        
                        return {
                            status: 'error',
                            message: [
                                `Invalid User defined premium entry: cell ${colLetter}${row + 1} has a value "${cellValue}" without a column header. Please add a header in "${colLetter}1" before entering data.`,
                            ],
                        };
                    }
                }
            }
            if (!rowHasAnyData) break;
        }

        return null;
    }

    private getColumnLetter(colIndex: number): string {
        let result = '';
        let col = colIndex + 1;
        while (col > 0) {
            const rem = (col - 1) % 26;
            result = String.fromCharCode(65 + rem) + result;
            col = Math.floor((col - 1) / 26);
        }
        return result;
    }

    private getFileConfig(workbook: any): any {
        const worksheet = workbook.worksheets(workbook.worksheets().count - 1);
        let i = 1;
        var config: any = {};
        var configKey;
        var configValue;
        do {
            configKey = worksheet.rows(i).cells(11).value; 
            configValue = worksheet.rows(i).cells(12).value; 
            config[configKey] = configValue;
            i++;
        }while (configValue != null && String(configValue).trim() !== ''); // Read until first empty cell in column A
        return config;
    }
}

